// pages/overlay/Reference.jsx
// Shows the reference image for the currently active drawing order so viewers
// can see what Dan is working from. Ties to the griddle's physical draw zones.
//
// Landscape: left ~55% empty (griddle cam in OBS), right ~45% reference panel.
// Portrait:  top ~50% empty (griddle cam), bottom ~50% reference panel.
//
// When no active drawing order: subtle placeholder ("Choosing next subject...").
// Crossfades when the active order changes.

import { useState, useEffect, useRef } from "react";
import useOrientation from "./../../hooks/useOrientation";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import useFlashDesigns from "./../../hooks/useFlashDesigns";
import { COLOR_OPTIONS } from "./../../flashDesigns";
import { computeDisplayTitle } from "./../../utils/meditations";

export default function Reference() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const { drawing, activeShift } = useCurrentShiftOrders();
  const { designMap } = useFlashDesigns(activeShift, { previewWhenIdle: true });

  // Track the previous order for crossfade
  const [displayOrder, setDisplayOrder] = useState(null);
  const [vis, setVis] = useState(true);
  const prevIdRef = useRef(null);

  useEffect(() => {
    const newId = drawing?.id ?? null;
    if (newId === prevIdRef.current) return;
    // Crossfade: fade out → swap → fade in
    setVis(false);
    const t = setTimeout(() => {
      setDisplayOrder(drawing);
      prevIdRef.current = newId;
      setVis(true);
    }, 400);
    return () => clearTimeout(t);
  }, [drawing]);

  const order = displayOrder;
  const name = computeDisplayTitle(order) || order?.subjectName || order?.recipientName || order?.customerName || "";
  const isFlash = order?.orderType === "flash" || order?.orderType === "freestyle_flash";
  const isFreestyle = order?.isFreestyle;

  // Reference image source
  let imgSrc = null;
  if (order && !isFreestyle) {
    if (isFlash && order.designId) {
      imgSrc = designMap[order.designId]?.imageUrl || `/flash-designs/${order.designId}.png`;
    } else {
      imgSrc = order.croppedImageUrl || order.imageUrl || null;
    }
  }

  // Color swatches
  const colorObjs = (order?.favoriteColors || [])
    .map(c => typeof c === "string" ? COLOR_OPTIONS.find(o => o.id === c) : c)
    .filter(Boolean);

  // Type label
  const typeLabel = !order ? ""
    : isFreestyle ? "✨ Freestyle"
    : order.orderType === "magic" ? "✨ Magic"
    : isFlash ? "⚡ Flash"
    : "🎨 Custom";

  return (
    <div
      data-orientation={orientation}
      style={{
        width: "100vw",
        height: "100vh",
        background: "transparent",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "calc(16px + 0.55vw)",
        userSelect: "none",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: portrait ? "column" : "row",
      }}
    >
      {/* JoP watermark */}
      <img
        src="/jop_logo_wide.png"
        alt=""
        style={{
          position: "absolute",
          bottom: "1.5rem",
          left: "1.5rem",
          height: "40px",
          opacity: 0.35,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Left/Top: empty — griddle camera shows through in OBS */}
      <div style={{ flex: portrait ? "0 0 50%" : "0 0 55%" }} />

      {/* Right/Bottom: reference panel */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: portrait ? "0.75rem 1.25rem" : "1.5rem 2rem 1.5rem 1rem",
        opacity: vis ? 1 : 0,
        transition: "opacity 0.4s ease",
        position: "relative",
      }}>
        {order ? (
          <>
            {/* Header: "Drawing for" + queue number */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: portrait ? "0.5rem" : "0.75rem",
              flexShrink: 0,
            }}>
              <div style={{
                background: "rgba(0,0,0,0.3)",
                borderRadius: "14px",
                padding: "0.75rem 1.2rem",
                border: "var(--jop-card-border)",
                boxShadow: "var(--jop-card-glow)",
              }}>
                <div style={{
                  color: "var(--jop-cream)",
                  fontSize: "1rem",
                  fontFamily: "var(--jop-display)",
                  fontStyle: "italic",
                }}>
                  ✨ making this for
                </div>
                <div style={{
                  color: "var(--jop-gold)",
                  fontWeight: 700,
                  fontSize: portrait ? "1.8rem" : "2.4rem",
                  fontFamily: "var(--jop-display)",
                  lineHeight: 1.15,
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}>
                  {name || "—"}
                </div>
              </div>
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "0.2rem",
              }}>
                <div style={{
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "10px",
                  padding: "0.25rem 0.7rem",
                  color: "white",
                  fontWeight: 800,
                  fontSize: "1.1rem",
                }}>
                  #{order.queueNumber}
                </div>
                <div style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}>{typeLabel}</div>
              </div>
            </div>

            {/* Reference image */}
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              minHeight: 0,
            }}>
              {isFreestyle ? (
                <div style={{
                  background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "16px",
                  padding: "2rem",
                  textAlign: "center",
                  maxWidth: "90%",
                }}>
                  <div style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: "0.75rem",
                  }}>Freestyle</div>
                  <div style={{
                    color: "white",
                    fontSize: "1.5rem",
                    fontStyle: "italic",
                    lineHeight: 1.4,
                  }}>
                    "{order.notes || order.freestyleDescription || "No description"}"
                  </div>
                </div>
              ) : imgSrc ? (
                <img
                  src={imgSrc}
                  alt="Reference"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "12px",
                    border: "2px solid rgba(255,255,255,0.15)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                    transform: isFlash ? "scaleX(-1)" : "none",
                  }}
                />
              ) : (
                <div style={{
                  color: "rgba(255,255,255,0.3)",
                  fontSize: "1.2rem",
                  fontStyle: "italic",
                }}>No reference image</div>
              )}

              {/* Color swatches — pinned to right edge of image area */}
              {colorObjs.length > 0 && (
                <div style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  alignItems: "center",
                }}>
                  <span style={{
                    color: "var(--jop-cream)",
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}>colors</span>
                  {colorObjs.map(color => (
                    <div key={color.id} style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px",
                    }}>
                      <div style={{
                        width: "52px",
                        height: "52px",
                        borderRadius: "50%",
                        background: color.hex,
                        boxShadow: "0 0 0 3px rgba(0,0,0,0.85), 0 3px 12px rgba(0,0,0,0.5)",
                      }} />
                      <span style={{
                        color: "white",
                        fontSize: "0.55rem",
                        fontWeight: "bold",
                        textShadow: "0 1px 3px rgba(0,0,0,0.9)",
                      }}>{color.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Subject label below image */}
            {order.designLabel && isFlash && (
              <div style={{
                textAlign: "center",
                marginTop: "0.5rem",
                color: "rgba(255,255,255,0.6)",
                fontSize: "1rem",
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {order.designLabel}
              </div>
            )}
          </>
        ) : (
          /* No active order — placeholder */
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
          }}>
            <div style={{ fontSize: "2.5rem" }}>🎨</div>
            <div style={{
              color: "rgba(255,255,255,0.35)",
              fontSize: "1.1rem",
              fontStyle: "italic",
            }}>
              Choosing next subject...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
